/**
 * Presentation helpers for the Sync Tracking screen.
 *
 * Mirrors the source design's status/direction/stage/event mappings, but returns
 * Tailwind class strings (built from the project's `mapper-*` design tokens)
 * instead of inline CSS. All class strings are written as complete literals so
 * Tailwind's JIT compiler can see them.
 */

import type { SyncTimelineEvent, SyncTrackingItem } from '../api/syncTrackingApi';

export type StatusGroup = 'active' | 'done' | 'attention' | 'failed' | 'acknowledged';

export interface StatusView {
    label: string;
    pill: string; // background + text classes for the status badge
    dot: string;  // background class for the status dot
    pulse: boolean;
    group: StatusGroup;
}

const STATUS_VIEWS: Record<string, StatusView> = {
    queued: { label: 'Queued', pill: 'bg-mapper-blue-bg text-mapper-blue', dot: 'bg-mapper-blue', pulse: false, group: 'active' },
    processing: { label: 'Processing', pill: 'bg-mapper-blue-bg text-mapper-blue', dot: 'bg-mapper-blue', pulse: true, group: 'active' },
    waiting_dependency: { label: 'Waiting on dependency', pill: 'bg-mapper-purple-bg text-mapper-purple', dot: 'bg-mapper-purple', pulse: false, group: 'active' },
    confirmation_pending: { label: 'Awaiting confirmation', pill: 'bg-mapper-blue-bg text-mapper-blue', dot: 'bg-mapper-blue', pulse: false, group: 'active' },
    succeeded: { label: 'Synced', pill: 'bg-mapper-teal-bg-3 text-mapper-teal-dark', dot: 'bg-mapper-green-dot', pulse: false, group: 'done' },
    ignored: { label: 'Skipped', pill: 'bg-mapper-gray-bg text-mapper-body-text-2', dot: 'bg-mapper-muted-3', pulse: false, group: 'done' },
    blocked: { label: 'Blocked', pill: 'bg-mapper-red-bg text-mapper-red', dot: 'bg-mapper-red-bar', pulse: false, group: 'attention' },
    retryable_failed: { label: 'Retrying', pill: 'bg-mapper-amber-bg text-mapper-amber', dot: 'bg-mapper-amber-bar', pulse: false, group: 'attention' },
    dead_letter: { label: 'Failed', pill: 'bg-mapper-red-bg-3 text-mapper-red-dark', dot: 'bg-mapper-red', pulse: false, group: 'failed' },
    cancelled: { label: 'Cancelled', pill: 'bg-mapper-gray-bg text-mapper-body-text-2', dot: 'bg-mapper-muted-3', pulse: false, group: 'failed' },
    acknowledged: { label: 'Acknowledged', pill: 'bg-mapper-gray-bg text-mapper-body-text', dot: 'bg-mapper-muted-3', pulse: false, group: 'acknowledged' },
};

export function statusView(status: string): StatusView {
    return STATUS_VIEWS[status] ?? STATUS_VIEWS.queued;
}

/** Sums a per-status count map (from the stats endpoint) down to one status group. */
export function sumByGroup(counts: Record<string, number>, group: StatusGroup): number {
    return Object.entries(counts).reduce(
        (sum, [status, n]) => statusView(status).group === group ? sum + n : sum,
        0,
    );
}

export interface DirView {
    from: string;
    to: string;
    label: string;
    fromPill: string;
    toPill: string;
}

const DIR_EMR_PILL = 'bg-mapper-purple-bg text-mapper-purple';
const DIR_CRM_PILL = 'bg-[#E8EDF2] text-mapper-body-text';

export function dirView(direction: string): DirView {
    return direction === 'emr_to_ghl'
        ? { from: 'EMR', to: 'Advital', label: 'EMR → Advital', fromPill: DIR_EMR_PILL, toPill: DIR_CRM_PILL }
        : { from: 'Advital', to: 'EMR', label: 'Advital → EMR', fromPill: DIR_CRM_PILL, toPill: DIR_EMR_PILL };
}

export interface FailBucket {
    label: string;
    hint: string;
    pill: string;
}

const FAIL_CONFIG = ['provider_mapping_missing', 'calendar_mapping_missing', 'status_mapping_missing', 'appointment_type_mapping_missing', 'emr_credentials_missing'];
const FAIL_DATA = ['patient_not_found', 'duplicate_patient_ambiguous', 'contact_already_mapped', 'invalid_source_payload', 'required_field_missing'];
const FAIL_TEMP = ['source_api_timeout', 'rate_limited', 'connection_error', 'service_unavailable', 'worker_interrupted', 'confirmation_timeout'];

export function failBucket(code: string): FailBucket {
    if (FAIL_CONFIG.includes(code)) return { label: 'Configuration', hint: 'Needs an admin mapping fix', pill: 'bg-mapper-amber-bg text-mapper-amber' };
    if (FAIL_DATA.includes(code)) return { label: 'Data / patient', hint: 'Record needs attention', pill: 'bg-mapper-red-bg text-mapper-red' };
    if (FAIL_TEMP.includes(code)) return { label: 'Temporary', hint: 'Will self-heal on retry', pill: 'bg-mapper-blue-bg text-mapper-blue' };
    return { label: 'Permanent', hint: 'Manual intervention required', pill: 'bg-mapper-red-bg-3 text-mapper-red-dark' };
}

/**
 * Presentation for an AI brief theme's root-cause category. Deliberately shares
 * the palette with `failBucket` above so a "Configuration" theme in the brief
 * reads the same as a configuration failure in the record drawer.
 */
export function briefCategoryView(category: string): FailBucket {
    switch (category) {
        case 'configuration': return { label: 'Configuration', hint: 'Needs an admin mapping fix', pill: 'bg-mapper-amber-bg text-mapper-amber' };
        case 'data': return { label: 'Data / patient', hint: 'Record needs attention', pill: 'bg-mapper-red-bg text-mapper-red' };
        case 'temporary': return { label: 'Temporary', hint: 'Will self-heal on retry', pill: 'bg-mapper-blue-bg text-mapper-blue' };
        default: return { label: 'Permanent', hint: 'Manual intervention required', pill: 'bg-mapper-red-bg-3 text-mapper-red-dark' };
    }
}

const STAGES: [string, string][] = [
    ['discovered', 'Discovered'], ['queued', 'Queued'], ['source_fetched', 'Source fetched'], ['validated', 'Validated'],
    ['patient_resolving', 'Resolving patient'], ['patient_resolved', 'Patient resolved'], ['provider_resolved', 'Provider resolved'],
    ['status_mapping_resolved', 'Status mapped'], ['calendar_mapping_resolved', 'Calendar mapped'], ['transformed', 'Transformed'],
    ['duplicate_checked', 'Duplicate checked'], ['destination_requested', 'Sent to destination'], ['confirmation_pending', 'Awaiting confirmation'],
    ['destination_confirmed', 'Confirmed'], ['binding_saved', 'Binding saved'], ['succeeded', 'Succeeded'],
];

export const STAGE_COUNT = STAGES.length;

export function stageLabel(stage: string): string {
    const found = STAGES.find(s => s[0] === stage);
    return found ? found[1] : stage;
}

export function stageIndex(stage: string): number {
    return STAGES.findIndex(s => s[0] === stage);
}

/** Percentage width (0–100) for the stage progress bar. */
export function stagePercent(stage: string): number {
    const idx = stageIndex(stage);
    return Math.round(((idx + 1) / STAGE_COUNT) * 100);
}

/** Progress-bar fill colour for a row, keyed off the status group. */
export function stageBarClass(status: string): string {
    const g = statusView(status).group;
    if (g === 'attention' || g === 'failed') return 'bg-mapper-red-bar';
    if (status === 'succeeded') return 'bg-mapper-green-dot';
    return 'bg-mapper-teal';
}

export interface EventView {
    title: string;
    dot: string;
}

export function eventView(ev: SyncTimelineEvent): EventView {
    switch (ev.type) {
        case 'claimed': return { title: 'Claimed by worker', dot: 'bg-mapper-teal' };
        case 'stage_changed': return { title: 'Stage → ' + stageLabel(ev.stage ?? ''), dot: 'bg-mapper-teal' };
        case 'retry_scheduled': return { title: 'Retry scheduled', dot: 'bg-mapper-amber-bar' };
        case 'manually_requeued': return { title: 'Manually requeued', dot: 'bg-mapper-purple' };
        case 'note': return { title: 'Note', dot: 'bg-mapper-muted-3' };
        case 'status_changed': {
            const sv = statusView(ev.to_status ?? '');
            return { title: 'Status → ' + sv.label, dot: sv.dot };
        }
        default: return { title: ev.type ?? 'Event', dot: 'bg-mapper-muted-3' };
    }
}

const TRIGGER_LABELS: Record<string, string> = {
    scheduled_poll: 'Scheduled',
    webhook: 'Webhook',
    manual: 'Manual',
    backfill: 'Backfill',
};

export function triggerLabel(trigger: string): string {
    return TRIGGER_LABELS[trigger] ?? trigger;
}

function cap(s: string): string {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function isPatientEntity(entityType: string): boolean {
    return (entityType || '').toLowerCase().includes('patient');
}

export function isAppointmentEntity(entityType: string): boolean {
    return (entityType || '').toLowerCase().includes('appointment');
}

/** The EMR/GHL reference ID shown for a record, e.g. "APT-88421". */
export function recordRef(item: SyncTrackingItem): string {
    return item.emr_id || item.ghl_id || `#${item.id}`;
}

/** Human-readable entity type, e.g. "Patient", "Appointment". */
export function entityTypeLabel(item: SyncTrackingItem): string {
    if (isAppointmentEntity(item.entity_type)) return 'Appointment';
    if (isPatientEntity(item.entity_type)) return 'Patient';
    return cap(item.entity_type || 'Record');
}

/**
 * Human-readable record title, derived from IDs only (the source table has no
 * name column). e.g. "Appointment · APT-88421", "Patient · PT-55190".
 */
export function recordLabel(item: SyncTrackingItem): string {
    return `${entityTypeLabel(item)} · ${recordRef(item)}`;
}

/** Secondary line: operation + trigger, e.g. "Create · Scheduled". */
export function recordSub(item: SyncTrackingItem): string {
    return `${cap(item.operation || '')} · ${triggerLabel(item.trigger)}`;
}

export function attemptTextClass(attempt: number, max: number): string {
    if (max > 0 && attempt >= max) return 'text-mapper-red font-bold';
    if (attempt > 1) return 'text-mapper-amber font-semibold';
    return 'text-mapper-muted-2';
}

/** Relative "time ago" from an ISO timestamp, e.g. "just now", "2m ago". */
export function timeAgo(iso: string | null): string {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '—';
    const diffSec = Math.floor((Date.now() - then) / 1000);
    if (diffSec < 45) return 'just now';
    const min = Math.floor(diffSec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return new Date(iso).toLocaleDateString();
}

/** Absolute timestamp for timeline events, e.g. "Jul 16, 2:04:09 PM". */
export function formatTimestamp(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit',
    });
}
